import { createFileRoute, Link } from '@tanstack/react-router';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import EmailIcon from '../assets/email.svg?react';
import Button from '../components/button/button';
import Error from '../components/inputs/error';
import TextInput from '../components/inputs/text-input';
import { LOGO_URL } from '../config/constants';
import { useForgotPasswordMutation } from '../services/auth';
import { mapToInputProps, submitHandler } from '../utils/form';
import { toastSuccess } from '../utils/toast';
import { emailRule, requiredRule, validateRules } from '../utils/validation';

import Box from '@/components/box';
import { useTranslation } from '@/translations/useTranslation';

type ForgotPasswordFormValues = {
  email: string;
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const forgotPasswordMutation = useForgotPasswordMutation();

  const form = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      email: '',
    },
  });

  return (
    <Box>
      <div className="mb-5 flex justify-center">
        <img src={LOGO_URL} className="max-h-52" />
      </div>

      <h2 className="mb-5 text-center text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
        {t('msg_forgot_password_form_title')}
      </h2>
      <form
        className="flex flex-col gap-4"
        onSubmit={submitHandler(form, async (values) => {
          await forgotPasswordMutation.mutateAsync(values);
          toastSuccess(t('msg_forgot_password_success'));
          form.reset();
        })}
      >
        <Controller
          name="email"
          control={form.control}
          rules={validateRules(emailRule(t), requiredRule(t))}
          render={(controllerProps) => (
            <TextInput
              {...mapToInputProps(controllerProps)}
              inputMode="email"
              icon={<EmailIcon width={22} height={22} />}
              label={t('msg_field_email')}
              placeholder={t('msg_field_email')}
            />
          )}
        />

        {form.formState.errors.root?.message && (
          <div className="mt-2">
            <Error error={form.formState.errors.root.message} />
          </div>
        )}
        <Button type="submit" loading={form.formState.isSubmitting} width="full">
          {t('msg_forgot_password_submit_button')}
        </Button>
      </form>

      <div className="mt-4 text-center">
        <p>
          <Link to="/login" className="text-primary">
            {t('msg_login_link')}
          </Link>
        </p>
      </div>
    </Box>
  );
};

export const Route = createFileRoute('/_public/forgot-password')({
  component: ForgotPassword,
});
